import { computed, ref, getCurrentInstance } from "vue";
import tcontractorJSONfile from "@/stores/auth/tcontractor.json";
import { useAuthStore } from "@/stores/auth/authStore";

/**
 * Composable for filtered translations using tcontractor.json
 * - Dynamically filters to only show accessed keys (prevents unused keys in Vue DevTools)
 * - Optionally filters by component path using used_in field
 * - Dynamic translations: All keys available (no filtering)
 */
export function useFilteredTranslations() {
  const authStore = useAuthStore();
  const instance = getCurrentInstance();
  
  // Get selected language from localStorage
  let lang = localStorage.getItem("lang");
  if (!lang) {
    lang = "english";
    localStorage.setItem("lang", "english");
  }
  const selectedLanguage = ref(lang);

  // Get component path for filtering
  const getComponentPath = () => {
    if (!instance) return null;
    const file = instance?.type?.__file || instance?.__file;
    if (!file) return null;
    const marker = "/resources/js/";
    const idx = file.lastIndexOf(marker);
    if (idx === -1) return null;
    const relative = file.substring(idx + marker.length);
    return relative.replace(/\.(vue|js|ts|tsx)$/i, "").replace(/\\/g, "/");
  };

  const componentPath = getComponentPath();

  // Build translations map from tcontractor.json
  const buildTranslationsMap = (lang) => {
    const langKey = lang === 'spanish' ? 'mexican_spanish' : 'english';
    const translationsMap = {};
    
    if (!Array.isArray(tcontractorJSONfile)) return translationsMap;
    
    tcontractorJSONfile.forEach(item => {
      if (!item.key || !item[langKey]) return;
      
      // If component path is available, filter by used_in
      if (componentPath) {
        const usedIn = item.used_in || [];
        const isUsedInComponent = usedIn.some(path => {
          // Exact match
          if (path === componentPath) return true;
          // Match with .vue extension
          if (path === `${componentPath}.vue`) return true;
          // Match without extension
          if (path.replace(/\.vue$/, '') === componentPath) return true;
          // Match if component path ends with the used_in path
          if (componentPath.endsWith(path) || componentPath.endsWith(path.replace(/\.vue$/, ''))) return true;
          return false;
        });
        
        if (isUsedInComponent) {
          translationsMap[item.key] = item[langKey];
        }
      } else {
        // If no component path, include all translations
        translationsMap[item.key] = item[langKey];
      }
    });
    
    return translationsMap;
  };

  // Dynamically track accessed keys using Proxy (no hardcoding)
  const accessedKeys = ref(new Set());
  const currentLang = ref(selectedLanguage.value);

  const createFilteredTranslations = (source, accessedKeysRef, currentLangRef, selectedLang) => {
    // Reset accessed keys when language changes
    if (currentLangRef.value !== selectedLang) {
      accessedKeysRef.value.clear();
      currentLangRef.value = selectedLang;
    }
    
    const filtered = {};
    
    return new Proxy(filtered, {
      get(target, prop) {
        if (typeof prop === 'string' && prop in source) {
          // Track accessed key and add to filtered object
          if (!accessedKeysRef.value.has(prop)) {
            accessedKeysRef.value.add(prop);
            filtered[prop] = source[prop];
          }
          return source[prop];
        }
        return target[prop];
      },
      ownKeys() {
        // Only return keys that have been accessed
        return Array.from(accessedKeysRef.value);
      },
      has(target, prop) {
        return accessedKeysRef.value.has(prop) && prop in source;
      },
      getOwnPropertyDescriptor(target, prop) {
        if (accessedKeysRef.value.has(prop) && prop in source) {
          return {
            enumerable: true,
            configurable: true,
            value: source[prop]
          };
        }
        return undefined;
      }
    });
  };

  // Filter tcontractor.json translations to only include accessed keys (prevents unused keys in Vue DevTools)
  const tcontractorTranslations = computed(() => {
    const allTranslations = buildTranslationsMap(selectedLanguage.value);
    return createFilteredTranslations(allTranslations, accessedKeys, currentLang, selectedLanguage.value);
  });

  // Dynamic translations from database - keep all (no filtering)
  const translations = computed(() => authStore.translationsGetter);

  // Fallback to database for text string if key not in tcontractor.json
  const getTranslation = (jsonTranslations, key) => {
    return jsonTranslations[key] || translations.value?.[key] || key;
  };

  // Both menuTranslation and indexTranslation use the same source now (tcontractor.json)
  const menuTranslation = (key) =>
    getTranslation(tcontractorTranslations.value, key);
  
  const indexTranslation = (key) =>
    getTranslation(tcontractorTranslations.value, key);

  // Update selected language
  const updateLanguage = (newLang) => {
    selectedLanguage.value = newLang;
    localStorage.setItem("lang", newLang);
  };

  return {
    tcontractorTranslations,
    translations,
    menuTranslation,
    indexTranslation,
    selectedLanguage,
    updateLanguage,
  };
}

